import {
    useContext,
    createContext,
    PropsWithChildren,
    useState,
    useEffect
} from "react";

import { getTemplateData } from "@/utils/content-queries/get-content-data";
import { getCurrentRoute } from "@/utils/get-current-route";

import { AGGClient } from "../../../clients";
import { AgileOverview } from "../types";

export const AgileOverviewContext = createContext<AgileOverview | null>(null);

const useAgileOverviewContextValue = (agileOverview: AgileOverview | null) => {
    const [contextValue, setContextValue] = useState(agileOverview);

    useEffect(() => {
        const fetchAgileOverviewFromClient = async () => {
            const client = new AGGClient({
                gatewayUrl:
                    process.env["NODE_ENV"] === "production"
                        ? "https://www.atlassian.com/gateway/api/graphql"
                        : "https://wac.stg.internal.atlassian.com/gateway/api/graphql"
            });

            const { route, locale } = getCurrentRoute();

            const agileOverview = await getTemplateData(route, locale, client);
            setContextValue(agileOverview);
        };

        if (!contextValue) {
            fetchAgileOverviewFromClient();
        }
        // empty dependency array so this effect only runs once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return contextValue;
};

export const AgileOverviewProvider = (
    props: PropsWithChildren<{ agileOverview: AgileOverview | null }>
) => {
    const value = useAgileOverviewContextValue(props.agileOverview);

    return (
        <AgileOverviewContext.Provider value={value}>
            {props.children}
        </AgileOverviewContext.Provider>
    );
};

export const useAgileOverview = (): AgileOverview | null => {
    const agileOverview = useContext(AgileOverviewContext);
    if (!agileOverview) {
        console.warn(
            "SSR failed to fetch content on the server. Fetching content on the client."
        );
    }
    return agileOverview;
};
